"use client";

import { useEffect, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";

/**
 * Detects Supabase auth tokens in URL hash fragments (#access_token=...)
 * and establishes the session, then redirects to dashboard.
 *
 * The @supabase/ssr browser client does NOT auto-detect hash fragments,
 * so we manually parse and call setSession().
 */
export default function AuthHashHandler() {
  const [processing, setProcessing] = useState(false);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;

    const hash = window.location.hash;
    if (!hash || !hash.includes("access_token")) return;

    // Parse hash params
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");

    if (!accessToken || !refreshToken) return;

    handled.current = true;

    const handleToken = async () => {
      setProcessing(true);
      const supabase = createClient();

      // Manually set the session from the hash tokens
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        console.error("Auth hash handler: failed to set session", error);
        setProcessing(false);
        return;
      }

      // Clear the hash from the URL then do a full navigation
      // Using window.location instead of router.replace to ensure
      // the page fully loads with the new session cookie
      window.location.replace("/dashboard");
    };

    void handleToken();
  }, []);

  // Show a loading state while processing to avoid flash of homepage
  if (processing) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0a0e1a",
          color: "#f1f5f9",
          fontFamily: "system-ui, sans-serif",
          fontSize: "1rem",
        }}
      >
        Setting up your account...
      </div>
    );
  }

  return null;
}

